import { memo, useState } from 'react';
import {
  FileStatusType,
  OrganizationFileResponse,
} from '@server/application/files/types/file.types';
import { Skeleton, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useNotificationContext } from '@/features/notifications';
import {
  AttachedFileDto,
  FileResponseDto,
  useGetAiAssistant,
  useGetOrganizationFiles,
} from '@/shared/api';
import { usePaging } from '@/shared/hooks';
import { useRouterContext } from '@/shared/router';
import { captureError } from '@/shared/utils/error-reporting';
import { KnowledgeBaseFileExplorer } from '@/widgets/knowledge-base/KnowledgeBaseFileExplorer';
import { KnowledgeBaseUploadSources } from './KnowledgeBaseUploadSources';

const hasPendingStatus = (file: FileResponseDto): boolean => {
  const { upload, vectorization, extraction } = (
    file as OrganizationFileResponse
  ).status;
  return [upload.status, vectorization.status, extraction.status].includes(
    FileStatusType.PENDING,
  );
};

interface KnowledgeBaseProps {
  onCancel?: () => void;
  onFilesSelect?: (files: AttachedFileDto[]) => void;
  isReadOnly?: boolean;
}

export const KnowledgeBase = memo(
  ({ isReadOnly, onFilesSelect, onCancel }: KnowledgeBaseProps) => {
    const navigate = useNavigate();
    const { activeOrganization } = useRouterContext();
    const { showNotification } = useNotificationContext();

    const { data: assistantData, isLoading: assistantDataLoading } =
      useGetAiAssistant(
        { pathParams: { id: activeOrganization?.id || '' } },
        { enabled: Boolean(activeOrganization?.id) },
      );

    const [searchTerm, setSearchTerm] = useState('');
    const [labelIds, setLabelIds] = useState<string[]>([]);
    const { page, pageSize, pageSizeOptions, onPagingChange } = usePaging({
      resetDeps: [searchTerm, labelIds],
    });
    const [isPolling, setIsPolling] = useState(false);
    const {
      data: filesData,
      isInitialLoading: filesInitializing,
      isFetching: filesFetching,
      isPreviousData,
    } = useGetOrganizationFiles(
      {
        pathParams: { organizationId: activeOrganization?.id || '' },
        queryParams: { labelIds, searchTerm, page, take: pageSize },
      },
      {
        keepPreviousData: true,
        refetchInterval: isPolling ? 10000 : undefined,
        onSuccess: ({ data }) => setIsPolling(data?.some(hasPendingStatus)),
        onError: (error) => {
          captureError(error, {
            module: 'knowledge_base',
            action: 'fetch_files',
            metadata: { organizationId: activeOrganization?.id },
          });
          showNotification({
            type: 'error',
            message: 'Failed to fetch files',
            description: 'Please try again later',
          });
        },
      },
    );

    return (
      <Skeleton loading={!assistantData || assistantDataLoading} active>
        <div>
          {!isReadOnly && <KnowledgeBaseUploadSources />}
          <div style={{ margin: '24px 0' }}>
            <Typography.Title level={4} style={{ marginBottom: 8 }}>
              {isReadOnly
                ? 'Select Knowledge Base Files'
                : 'Knowledge Base Files'}
            </Typography.Title>
            <Typography.Text type="secondary">
              {isReadOnly ? (
                <>
                  Select files from your knowledge base to provide context to
                  the AI assistant for this specific process. To add more files,
                  visit the{' '}
                  <Typography.Link
                    onClick={() => {
                      onCancel?.();
                      navigate('/dashboard/knowledge-base');
                    }}
                  >
                    organization settings
                  </Typography.Link>{' '}
                  and click on the Knowledge Base tab.
                </>
              ) : (
                "Manage your organization's knowledge base files. You can upload local files or connect to external sources like Google Drive to import documents. These files will be used to train your AI assistant."
              )}
            </Typography.Text>
          </div>
          <KnowledgeBaseFileExplorer
            files={filesData?.data as OrganizationFileResponse[]}
            total={filesData?.meta.itemCount}
            isLoading={filesInitializing || (filesFetching && isPreviousData)}
            isPolling={isPolling}
            isReadOnly={isReadOnly}
            onFilesSelect={onFilesSelect}
            page={page}
            pageSize={pageSize}
            pageSizeOptions={pageSizeOptions}
            onPagingChange={onPagingChange}
            searchTerm={searchTerm}
            onSearchTermChange={setSearchTerm}
            selectedTags={labelIds}
            onSelectedTagsChange={setLabelIds}
          />
        </div>
      </Skeleton>
    );
  },
);

KnowledgeBase.displayName = 'KnowledgeBase';
